import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Info, User, Activity, Users, Radio, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LivePerformanceDialog from '@/components/analytics/LivePerformanceDialog';
import LiveAudienceDialog from '@/components/analytics/LiveAudienceDialog';
import LiveStreamHistoryDialog from '@/components/analytics/LiveStreamHistoryDialog';
import LiveRecommendationsDialog from '@/components/analytics/LiveRecommendationsDialog';

const API = window.__BACKEND_URL;

function DashboardTile({ icon: Icon, title, summary, onClick, accent }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-xl border border-white/10 bg-white/[0.02] p-4 transition-colors hover:bg-white/5 hover:border-blue-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A7BFF]/60"
      data-testid={`live-tile-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent}`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <p className="text-sm font-semibold text-white">{title}</p>
      <p className="text-xs text-[#8A93A6] mt-1">{summary}</p>
    </button>
  );
}

export default function AnalyzeLive() {
  const { user } = useContext(AuthContext);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [session, setSession] = useState('last');
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState('all');
  const [openPanel, setOpenPanel] = useState(null); // 'performance' | 'audience' | 'history' | 'recommendations' | null

  // Same plan-tier check used on regular Analytics — admin status never
  // grants selector access on its own.
  const isAgency = user?.plan_tier && ['agency', 'agency_elite', 'enterprise'].includes(user.plan_tier);

  useEffect(() => {
    const fetchCreator = async () => {
      try {
        const activeId = localStorage.getItem('activeCreatorId');
        if (activeId) {
          const res = await fetch(`${API}/api/creators/${activeId}`, { credentials: 'include' });
          if (res.ok) setCreatorInfo(await res.json());
        }
        if (!creatorInfo) {
          const res = await fetch(`${API}/api/creator-profile`, { credentials: 'include' });
          if (res.ok) setCreatorInfo(await res.json());
        }
      } catch {}
    };
    fetchCreator();
  }, []);

  useEffect(() => {
    if (isAgency) {
      fetch(`${API}/api/creators`, { credentials: 'include' })
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => setCreators(Array.isArray(data) ? data : data?.creators || []))
        .catch(() => setCreators([]));
    }
  }, [isAgency]);

  const sessions = [
    { value: 'current', label: 'Current Live' },
    { value: 'last', label: 'Last Live' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
  ];
  const sessionLabel = sessions.find((s) => s.value === session)?.label || 'Last Live';

  // Live metrics have no per-creator separation in the backend yet —
  // selecting a creator here only updates the displayed identity, using
  // the real creator record, never fabricated performance numbers.
  const selectedCreatorRecord = isAgency && selectedCreator !== 'all' ? creators.find((c) => c.id === selectedCreator) : null;
  const displayName = selectedCreatorRecord?.name || selectedCreatorRecord?.display_name || creatorInfo?.name || creatorInfo?.display_name || 'Creator';
  const displayHandle = selectedCreatorRecord?.handle || creatorInfo?.handle || creatorInfo?.username || '@creator';

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-[#8A93A6] tracking-wide mb-2">
          <Link to="/app/analyze" className="hover:text-white transition-colors">Analyze</Link>
          <span className="opacity-50">/</span>
          <span className="text-[#C0C7D4]">Live Analytics</span>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-white">Live Analytics</h1>
          <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-400 uppercase tracking-wider font-semibold">Read-only</Badge>
        </div>
        <p className="text-[#8A93A6] text-sm">Real-time observation of live stream performance</p>
      </div>

      {/* Creator Context + Session Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3" data-testid="live-creator-context">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white uppercase tracking-wide">
              {displayName}
            </p>
            <p className="text-xs text-[#8A93A6]">
              {displayHandle}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5" data-testid="session-selector">
          {sessions.map((s) => (
            <button
              key={s.value}
              onClick={() => setSession(s.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                session === s.value
                  ? 'bg-green-500/20 text-green-400'
                  : 'text-[#8A93A6] hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Creator Selector for Agency users — same pattern as regular Analytics */}
      {isAgency && creators.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-3" data-testid="live-creator-selector-row">
            <label className="text-sm text-[#8A93A6]">Viewing analytics for:</label>
            <Select value={selectedCreator} onValueChange={setSelectedCreator}>
              <SelectTrigger className="w-[200px] bg-[#1C1F26] border-white/10 text-white" data-testid="live-creator-selector">
                <SelectValue placeholder="Select creator" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Creators</SelectItem>
                {creators.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.display_name || 'Unnamed'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-[#555E6E] mt-2" data-testid="live-no-per-creator-note">
            Live metrics below are account-level and not yet separated per creator.
          </p>
        </div>
      )}

      {/* Info Alert */}
      <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-400 text-sm">
          <strong>Read-only observation:</strong> This section provides real-time metrics during live streams.
          No controls or moderation tools are available here.
        </AlertDescription>
      </Alert>

      {/* Live Intelligence */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Live Intelligence</h2>
        <p className="text-xs text-[#8A93A6] mb-4">Showing: {sessionLabel}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="live-intelligence-tiles">
          <DashboardTile
            icon={Activity} title="Live Performance" accent="bg-green-500/10 text-green-400"
            summary="Viewer counts, duration, engagement & gifts"
            onClick={() => setOpenPanel('performance')}
          />
          <DashboardTile
            icon={Users} title="Audience Intelligence" accent="bg-blue-500/10 text-blue-400"
            summary="Audience activity and engagement"
            onClick={() => setOpenPanel('audience')}
          />
          <DashboardTile
            icon={Radio} title="Stream History" accent="bg-purple-500/10 text-purple-400"
            summary="Previous live sessions and performance"
            onClick={() => setOpenPanel('history')}
          />
          <DashboardTile
            icon={Sparkles} title="Live Recommendations" accent="bg-amber-500/10 text-amber-400"
            summary="Actionable live-stream improvement suggestions"
            onClick={() => setOpenPanel('recommendations')}
          />
        </div>
      </div>

      <LivePerformanceDialog open={openPanel === 'performance'} onOpenChange={(v) => setOpenPanel(v ? 'performance' : null)} sessionLabel={sessionLabel} />
      <LiveAudienceDialog open={openPanel === 'audience'} onOpenChange={(v) => setOpenPanel(v ? 'audience' : null)} />
      <LiveStreamHistoryDialog open={openPanel === 'history'} onOpenChange={(v) => setOpenPanel(v ? 'history' : null)} sessionLabel={sessionLabel} />
      <LiveRecommendationsDialog open={openPanel === 'recommendations'} onOpenChange={(v) => setOpenPanel(v ? 'recommendations' : null)} />
    </div>
  );
}
